import { Injectable, signal } from '@angular/core';
import { Subject, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class InactivityService {
  private readonly INACTIVITY_LIMIT = 10 * 60 * 1000; // 10 minutes
  private readonly WARNING_TIME = 9 * 60 * 1000; // 9 minutes
  private readonly activityEvents = [ 'keydown', 'mousedown', 'touchstart'];
  private readonly warningSubject = new Subject<number>();
  private readonly logoutSubject = new Subject<void>();
  private intervalId?: ReturnType<typeof setInterval>;
  private readonly lastActivity = signal(Date.now());
  private readonly isStarted = signal(false);
  private readonly boundResetTimer = this.resetTimer.bind(this);

  /**
   * Start tracking inactivity. Should only be called after user is authenticated.
   */
  public start(): void {
    if (this.isStarted()) {
      return;
    }
    this.isStarted.set(true);
    this.activityEvents.forEach(event => {
      globalThis.addEventListener(event, this.boundResetTimer);
    });
    this.startTimer();
  }

  private startTimer(): void {
    this.clearTimers();
    this.lastActivity.set(Date.now());
    this.intervalId = setInterval(() => {
      const now = Date.now();
      const inactiveMs = now - this.lastActivity();
      if (inactiveMs >= this.INACTIVITY_LIMIT) {
        this.logoutSubject.next();
        this.clearTimers();
      } else if (inactiveMs >= this.WARNING_TIME) {
        // Only emit warning once per inactivity period
        if (inactiveMs - this.WARNING_TIME < 1000) {
          this.warningSubject.next(Math.floor((this.INACTIVITY_LIMIT - inactiveMs) / 1000));
        }
      }
    }, 1000);
  }

  public resetTimer(): void {
    this.lastActivity.set(Date.now());
  }

  private clearTimers(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  get warning$(): Observable<number> {
    return this.warningSubject.asObservable();
  }

  get logout$(): Observable<void> {
    return this.logoutSubject.asObservable();
  }

  stop(): void {
    this.clearTimers();
    this.isStarted.set(false);
    this.activityEvents.forEach(event => {
      globalThis.removeEventListener(event, this.boundResetTimer);
    });
  }
}
